import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    api,
    setToken,
    setStoredUser,
    isSystemUnlocked,
    setSystemUnlocked,
    clearSystemUnlock,
} from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    LockKey,
    User as UserIcon,
    ArrowRight,
    ArrowLeft,
    ShieldCheck,
} from "@phosphor-icons/react";

const BG_IMG =
    "https://static.prod-images.emergentagent.com/jobs/466214f1-5f18-4349-b3e2-fe83bc16ae75/images/c7fc1ba5cb20aeedd4d3aa9801ce9492123d95942525f477e6ebec2f21666178.png";

const TAMEIAKES_LOGO =
    "https://customer-assets.emergentagent.com/job_client-charge-system/artifacts/fqsds6nb_%CE%9A%CE%91%CE%91%CE%A1%CE%A4%CE%91.jpg";

export default function Login() {
    // step 1 = system unlock (supervisor creds), step 2 = operator picks self
    const [step, setStep] = useState(() => (isSystemUnlocked() ? 2 : 1));
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { setUser } = useAuth();

    // Step 1 state
    const [sysUsername, setSysUsername] = useState("");
    const [sysPassword, setSysPassword] = useState("");

    // Step 2 state
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [opPassword, setOpPassword] = useState("");
    const [license, setLicense] = useState(null); // {days_remaining, is_set, severity, ...}

    // Lookup table for selected username
    const selectedUser = useMemo(
        () => users.find((u) => u.id === selectedUserId) || null,
        [users, selectedUserId]
    );

    useEffect(() => {
        if (step !== 2) return;
        api
            .get("/auth/users-public")
            .then((r) => setUsers(r.data || []))
            .catch(() => {
                toast.error("Δεν ήταν δυνατή η φόρτωση χρηστών");
            });
        // Fetch license status for the banner — public endpoint, no auth
        api
            .get("/license/public")
            .then((r) => setLicense(r.data || null))
            .catch(() => setLicense(null));
    }, [step]);

    const handleStep1 = async (e) => {
        e.preventDefault();
        if (!sysUsername.trim() || !sysPassword.trim()) {
            toast.error("Συμπληρώστε όνομα χρήστη και κωδικό");
            return;
        }
        setLoading(true);
        try {
            // Validate by calling the login endpoint — accept ONLY supervisor
            const { data } = await api.post("/auth/login", {
                username: sysUsername.trim(),
                password: sysPassword,
            });
            if (!data?.user?.is_supervisor) {
                toast.error("Μόνο ο διαχειριστής μπορεί να ξεκλειδώσει το σύστημα");
                return;
            }
            // Mark system unlocked. Don't keep the supervisor token; user will
            // pick their operator account at step 2.
            setSystemUnlocked();
            setSysUsername("");
            setSysPassword("");
            setStep(2);
            toast.success("Σύστημα ξεκλειδώθηκε");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Λάθος στοιχεία");
        } finally {
            setLoading(false);
        }
    };

    const handleStep2 = async (e) => {
        e.preventDefault();
        if (!selectedUser) {
            toast.error("Επιλέξτε χρήστη");
            return;
        }
        if (!opPassword.trim()) {
            toast.error("Εισάγετε τον κωδικό σας");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", {
                username: selectedUser.username,
                password: opPassword,
            });
            setToken(data.token);
            setStoredUser(data.user);
            setUser(data.user);
            toast.success(`Καλώς ήρθες ${data.user?.name || ""}`);
            navigate("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Λάθος κωδικός");
        } finally {
            setLoading(false);
        }
    };

    const handleLockSystem = () => {
        clearSystemUnlock();
        setStep(1);
        setOpPassword("");
        setSelectedUserId("");
    };

    return (
        <div
            className="min-h-screen w-full grid lg:grid-cols-2 bg-white"
            data-testid="login-page"
        >
            {/* LEFT — visual panel */}
            <div className="relative hidden lg:flex flex-col justify-between p-10 border-r border-[#E2E4E9] bg-[#F4F5F7] overflow-hidden">
                <div
                    className="absolute inset-0 opacity-90"
                    style={{
                        backgroundImage: `url(${BG_IMG})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-white/5" />
                <div className="relative z-10">
                    <div
                        className="text-xs font-bold uppercase tracking-[0.3em] text-[#0A0A0A]"
                        data-testid="brand-tag"
                    >
                        MyCreditManager.gr
                    </div>
                </div>
                <div className="relative z-10 max-w-md fade-up">
                    <h1 className="font-heading text-5xl xl:text-6xl font-black tracking-tighter text-[#0A0A0A] leading-[0.95]">
                        Διαχείριση
                        <br />
                        Πιστώσεων
                        <br />
                        <span className="text-[#002FA7]">Πελατών.</span>
                    </h1>
                </div>
                <div className="relative z-10 flex gap-2">
                    <div className="h-2 w-12 bg-[#0A0A0A]" />
                    <div className="h-2 w-4 bg-[#002FA7]" />
                    <div className="h-2 w-2 bg-[#FF3B30]" />
                </div>
            </div>

            {/* RIGHT — form */}
            <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-12 relative">
                <div className="max-w-md w-full mx-auto fade-up">
                    {/* Top brand for mobile (left panel hidden) */}
                    <div className="lg:hidden mb-6 text-center">
                        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#002FA7]">
                            MyCreditManager.gr
                        </div>
                    </div>

                    {step === 1 ? (
                        <>
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5F66] mb-3">
                                Βήμα 1 από 2 · Ξεκλείδωμα συστήματος
                            </div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]">
                                Ξεκλειδώστε
                                <br />
                                το τερματικό
                            </h2>
                            <p className="mt-3 text-sm text-[#5C5F66]">
                                Εισάγετε τα στοιχεία διαχειριστή για να ενεργοποιηθεί
                                το τερματικό για όλη τη βάρδια.
                            </p>

                            <form onSubmit={handleStep1} className="mt-10 space-y-5">
                                <div className="space-y-2">
                                    <Label
                                        htmlFor="sys-username"
                                        className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]"
                                    >
                                        Ονομα διαχειριστη
                                    </Label>
                                    <div className="relative">
                                        <UserIcon
                                            size={18}
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F66]"
                                            weight="regular"
                                        />
                                        <Input
                                            id="sys-username"
                                            type="text"
                                            autoFocus
                                            autoComplete="off"
                                            spellCheck={false}
                                            value={sysUsername}
                                            onChange={(e) => setSysUsername(e.target.value)}
                                            placeholder="Όνομα διαχειριστή"
                                            data-testid="login-system-username"
                                            className="rounded-none h-12 pl-10 border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7] text-base"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label
                                        htmlFor="sys-password"
                                        className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]"
                                    >
                                        Κωδικος
                                    </Label>
                                    <div className="relative">
                                        <LockKey
                                            size={18}
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F66]"
                                            weight="regular"
                                        />
                                        <Input
                                            id="sys-password"
                                            type="password"
                                            autoComplete="off"
                                            value={sysPassword}
                                            onChange={(e) => setSysPassword(e.target.value)}
                                            placeholder="••••••••"
                                            data-testid="login-system-password"
                                            className="rounded-none h-12 pl-10 border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7] text-base"
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="login-system-submit"
                                    className="w-full h-12 rounded-none bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white font-semibold tracking-wide transition-all duration-200 active:scale-[0.98] group"
                                >
                                    <ShieldCheck size={18} weight="bold" className="mr-2" />
                                    {loading ? "Έλεγχος..." : "Ξεκλείδωμα"}
                                    <ArrowRight
                                        size={18}
                                        weight="bold"
                                        className="ml-2 transition-transform duration-200 group-hover:translate-x-1"
                                    />
                                </Button>
                            </form>
                        </>
                    ) : (
                        <>
                            {/* License status banner — top-right of the form area */}
                            <LicenseBanner license={license} />
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5F66] mb-3">
                                Βήμα 2 από 2 · Είσοδος χειριστή
                            </div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]">
                                Επιλέξτε
                                <br />
                                χρήστη
                            </h2>
                            <p className="mt-3 text-sm text-[#5C5F66]">
                                Διαλέξτε το όνομά σας και εισάγετε τον προσωπικό σας
                                κωδικό.
                            </p>

                            <form onSubmit={handleStep2} className="mt-10 space-y-5">
                                <div className="space-y-2">
                                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]">
                                        Χρηστης
                                    </Label>
                                    <Select
                                        value={selectedUserId}
                                        onValueChange={setSelectedUserId}
                                    >
                                        <SelectTrigger
                                            data-testid="login-user-select"
                                            className="rounded-none h-12 border-[#E2E4E9] focus:ring-1 focus:ring-[#002FA7] text-base"
                                        >
                                            <SelectValue placeholder="-- Επιλέξτε χρήστη --" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {users.map((u) => (
                                                <SelectItem
                                                    key={u.id}
                                                    value={u.id}
                                                    data-testid={`login-user-option-${u.id}`}
                                                >
                                                    {u.name}
                                                    {u.is_supervisor ? "  ·  Διαχειριστής" : ""}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label
                                        htmlFor="op-password"
                                        className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]"
                                    >
                                        Κωδικος
                                    </Label>
                                    <div className="relative">
                                        <LockKey
                                            size={18}
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F66]"
                                            weight="regular"
                                        />
                                        <Input
                                            id="op-password"
                                            type="password"
                                            autoComplete="off"
                                            value={opPassword}
                                            onChange={(e) => setOpPassword(e.target.value)}
                                            placeholder="••••••••"
                                            data-testid="login-user-password"
                                            className="rounded-none h-12 pl-10 border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7] text-base"
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="login-user-submit"
                                    className="w-full h-12 rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white font-semibold tracking-wide transition-all duration-200 active:scale-[0.98] group"
                                >
                                    {loading ? "Σύνδεση..." : "Σύνδεση"}
                                    <ArrowRight
                                        size={18}
                                        weight="bold"
                                        className="ml-2 transition-transform duration-200 group-hover:translate-x-1"
                                    />
                                </Button>

                                <button
                                    type="button"
                                    onClick={handleLockSystem}
                                    data-testid="lock-system-button"
                                    className="w-full text-xs font-bold uppercase tracking-[0.15em] text-[#5C5F66] hover:text-[#0A0A0A] transition-colors flex items-center justify-center gap-1.5 mt-2"
                                >
                                    <ArrowLeft size={14} weight="bold" />
                                    Κλείδωμα συστήματος
                                </button>
                            </form>
                        </>
                    )}

                    <div className="mt-12 pt-6 border-t border-[#E2E4E9] text-xs text-[#5C5F66]">
                        Προστατευμένη περιοχή · Όλες οι ενέργειες
                        καταγράφονται.
                    </div>
                </div>

                {/* Bottom-right footer — logo + tameiakes link */}
                <div
                    className="absolute bottom-6 right-6 sm:bottom-8 sm:right-10 lg:right-16 flex items-center gap-3"
                    data-testid="tameiakes-footer"
                >
                    <a
                        href="https://www.tameiakes.gr"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="tameiakes-link"
                        className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C5F66] hover:text-[#002FA7] transition-colors whitespace-nowrap text-right"
                    >
                        © 2026 Created
                        <br />
                        by tameiakes.gr ↗
                    </a>
                    <img
                        src={TAMEIAKES_LOGO}
                        alt="Κυριακίδης — Μηχανές Γραφείου"
                        data-testid="tameiakes-logo"
                        className="w-44 h-auto"
                    />
                </div>
            </div>
        </div>
    );
}


function LicenseBanner({ license }) {
    if (!license || !license.is_set) return null;
    const days = license.days_remaining;
    const sev = license.severity;

    const styles = {
        ok: {
            bg: "bg-[#34C759]",
            border: "border-[#1F8A3E]",
            text: "text-white",
            label: "ΑΠΟΜΕΝΟΥΝ",
            message: null,
        },
        warn: {
            bg: "bg-[#FF9500]",
            border: "border-[#C76F00]",
            text: "text-white",
            label: "ΑΠΟΜΕΝΟΥΝ",
            message: "Ανανεώστε την άδεια του προγράμματος",
        },
        urgent: {
            bg: "bg-[#FF3B30]",
            border: "border-[#A4291F]",
            text: "text-white",
            label: "ΑΠΟΜΕΝΟΥΝ",
            message: "Ανανεώστε άμεσα την άδεια του προγράμματος",
        },
        expired: {
            bg: "bg-[#0A0A0A]",
            border: "border-[#FF3B30]",
            text: "text-white",
            label: "ΛΗΞΕ",
            message: "Η άδεια λειτουργίας του προγράμματος έληξε",
        },
    };
    const s = styles[sev] || styles.ok;
    const displayDays = sev === "expired" ? 0 : Math.max(0, days);

    return (
        <div
            className={`mb-6 ${s.bg} ${s.text} border-4 ${s.border} px-5 py-4 fade-up`}
            data-testid="license-banner"
            data-severity={sev}
        >
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] opacity-90">
                    {s.label}
                </div>
                <div className="font-heading font-black tracking-tighter text-3xl sm:text-4xl leading-none tabular-nums">
                    {displayDays}
                    <span className="text-base font-bold ml-2 opacity-90">
                        {sev === "expired" ? "ΗΜΕΡΕΣ" : "ΗΜΕΡΕΣ ΛΕΙΤΟΥΡΓΙΑΣ"}
                    </span>
                </div>
            </div>
            {s.message && (
                <div
                    className="mt-2 text-sm font-bold uppercase tracking-[0.1em] border-t border-white/30 pt-2"
                    data-testid="license-banner-message"
                >
                    ⚠ {s.message}
                </div>
            )}
        </div>
    );
}
